import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, XCircle, RefreshCw, Loader2, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LoadErrorBanner, { describeLoadError } from "@/components/admin/LoadErrorBanner";

export const REG_TYPES = ["ABN", "ACN", "NZBN", "Company No.", "Other"];

export const VerificationStatusPill = ({ status, verified }) => {
  const s = verified ? "verified" : status || "";
  const map = {
    verified: "bg-emerald-50 text-emerald-800 border-emerald-300",
    pending: "bg-amber-100 text-amber-800 border-amber-300",
    rejected: "bg-red-50 text-red-800 border-red-300",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest font-semibold border px-2 py-0.5 ${map[s] || "bg-stone-100 text-stone-600 border-stone-300"}`} data-testid="verification-status-pill">
      {s || "not requested"}
    </span>
  );
};

// Shared decision editor — used by the Verification tab and the Listings table popover.
export const VerificationEditor = ({ target, onDone, onCancel }) => {
  const [type, setType] = useState(target.business_registration_number_type || "ABN");
  const [number, setNumber] = useState(target.business_registration_number || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [abr, setAbr] = useState({ state: "idle", message: "", entity: null }); // idle | checking | ok | error | unconfigured
  const [abrConfigured, setAbrConfigured] = useState(null);
  const url = target.listing_id ? `/admin/listings/${target.listing_id}/verification` : `/admin/suppliers/${target.user_id}/verification`;
  const abnUrl = target.listing_id ? `/admin/listings/${target.listing_id}/verify-abn` : `/admin/suppliers/${target.user_id}/verify-abn`;
  const digits = number.replace(/\D/g, "");
  const isAbn = type === "ABN";

  useEffect(() => {
    api.get("/admin/abr/status").then(({ data }) => setAbrConfigured(!!data.configured)).catch(() => setAbrConfigured(false));
  }, []);

  // ABN: as soon as 11 digits are typed, look it up on the ABR and auto-verify on an active match.
  useEffect(() => {
    if (!isAbn || target.is_verified || digits.length !== 11 || abrConfigured === null) { if (abr.state !== "idle") setAbr({ state: "idle", message: "", entity: null }); return; }
    if (!abrConfigured) { setAbr({ state: "unconfigured", message: "ABR lookup not configured (add ABR_GUID) — use Mark Verified.", entity: null }); return; }
    let alive = true;
    setAbr({ state: "checking", message: "Checking the Australian Business Register…", entity: null });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(abnUrl, { abn: digits });
        if (!alive) return;
        setAbr({ state: "ok", message: `Registered entity: ${data.abr?.entity_name || "(name suppressed)"} · ${data.abr?.status}`, entity: data.abr?.entity_name });
        toast.success(`ABN verified — ${data.abr?.entity_name || "active on ABR"}`);
        onDone && onDone();
      } catch (e) {
        if (!alive) return;
        setAbr({ state: "error", message: e.response?.data?.detail || "ABR lookup failed", entity: null });
      }
    }, 500);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits, isAbn, abrConfigured]);

  const act = async (action) => {
    if (action === "approve" && number.trim().length < 4 && !window.confirm("No registration number entered. Mark as Verified anyway?")) return;
    if (action === "reject" && !window.confirm("Reject this verification? The supplier will be emailed your note.")) return;
    if (action === "unverify" && !window.confirm("Remove the Verified Supplier badge from all their listings?")) return;
    setBusy(action);
    try {
      await api.patch(url, { action, note, registration_number: number, registration_type: type });
      toast.success(action === "approve" ? "Marked as Verified Supplier" : action === "reject" ? "Verification rejected" : "Verification removed");
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-white border border-[#C4CFC9] p-4 max-w-xl text-left" data-testid="verification-editor">
      <div className="text-xs text-[#7D8C85] mb-2">Registration details (editable)</div>
      <div className="flex gap-2 mb-2">
        <select value={type} onChange={(e) => setType(e.target.value)} className="px-2 py-1.5 text-sm border border-[#C4CFC9] bg-white rounded-sm" data-testid="verification-editor-type">
          {REG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder={isAbn ? "ABN — 11 digits, auto-checked on the ABR" : "Registration number"} className="flex-1 px-2 py-1.5 text-sm border border-[#C4CFC9] rounded-sm" data-testid="verification-editor-number" />
      </div>
      {isAbn && abr.state !== "idle" && (
        <div className={`text-xs mb-2 flex items-start gap-1.5 ${abr.state === "ok" ? "text-emerald-700" : abr.state === "error" ? "text-[#B35C4A]" : "text-[#7D8C85]"}`} data-testid={`abr-status-${abr.state}`}>
          {abr.state === "checking" && <Loader2 className="w-3.5 h-3.5 animate-spin mt-0.5 shrink-0" />}
          {abr.state === "ok" && <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
          {abr.state === "error" && <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
          <span>{abr.message}</span>
        </div>
      )}
      {isAbn && abr.state === "idle" && !target.is_verified && digits.length > 0 && digits.length < 11 && (
        <div className="text-xs text-[#7D8C85] mb-2" data-testid="abr-hint">{11 - digits.length} more digit{11 - digits.length === 1 ? "" : "s"} — the ABR check runs automatically and verifies on an active match.</div>
      )}
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note to the supplier (sent by email)" className="w-full px-2 py-1.5 text-sm border border-[#C4CFC9] rounded-sm" data-testid="verification-editor-note" />
      <div className="flex flex-wrap gap-1.5 mt-3">
        {!target.is_verified && !(isAbn && abrConfigured) && (
          <button onClick={() => act("approve")} disabled={!!busy} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-emerald-600 text-white hover:bg-emerald-700 px-2.5 py-1.5 transition disabled:opacity-50" data-testid="verification-approve-btn">
            <ShieldCheck className="w-3 h-3" /> {busy === "approve" ? "…" : "Mark Verified"}
          </button>
        )}
        {target.verification_status === "pending" && (
          <button onClick={() => act("reject")} disabled={!!busy} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-[#B35C4A] text-white hover:bg-[#9E4E3E] px-2.5 py-1.5 transition disabled:opacity-50" data-testid="verification-reject-btn">
            <XCircle className="w-3 h-3" /> {busy === "reject" ? "…" : "Reject"}
          </button>
        )}
        {target.is_verified && (
          <button onClick={() => act("unverify")} disabled={!!busy} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border border-[#B35C4A] text-[#B35C4A] hover:bg-[#B35C4A] hover:text-white px-2.5 py-1.5 transition disabled:opacity-50" data-testid="verification-unverify-btn">
            <ShieldOff className="w-3 h-3" /> {busy === "unverify" ? "…" : "Unverify"}
          </button>
        )}
        <button onClick={onCancel} className="ml-auto text-[10px] uppercase tracking-widest text-[#7D8C85] hover:text-[#11211A] px-2 py-1.5" data-testid="verification-editor-cancel">Cancel</button>
      </div>
    </div>
  );
};

export const ManualVerifyCard = ({ onChanged }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const search = async (e) => {
    e?.preventDefault();
    const term = q.trim();
    if (term.length < 2) { toast.error("Type at least 2 characters of the email or company"); return; }
    setBusy(true); setOpen(null);
    try { const { data } = await api.get(`/admin/verifications/lookup?q=${encodeURIComponent(term)}`); setItems(data.items || []); }
    catch (e2) { toast.error(e2.response?.data?.detail || "Lookup failed"); }
    finally { setBusy(false); }
  };
  const done = () => { setOpen(null); search(); onChanged && onChanged(); };
  return (
    <div className="bg-white border border-[#264839]/40 p-4 mb-5" data-testid="manual-verify-card">
      <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">Manual verification</div>
      <div className="font-serif text-lg text-[#11211A] mt-0.5">Verify any supplier by email — no request needed</div>
      <p className="text-xs text-[#7D8C85] mt-1">Search by email address or company name, then Mark Verified. The badge applies to all their listings immediately and the supplier receives the standard "You're now a Verified Supplier" email.</p>
      <form onSubmit={search} className="mt-3 flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="supplier@example.com or company name" className="flex-1 min-w-[260px] px-3 py-2 text-sm border border-[#C4CFC9] bg-white rounded-sm" data-testid="manual-verify-search-input" />
        <button type="submit" disabled={busy} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest border border-[#264839] text-[#264839] px-3 py-2 hover:bg-[#264839] hover:text-white transition disabled:opacity-50" data-testid="manual-verify-search-btn"><Search className="w-3 h-3" /> {busy ? "Searching…" : "Search"}</button>
      </form>
      {items && (
        <div className="mt-3 divide-y divide-[#F0EFEA] border border-[#E8ECE9]" data-testid="manual-verify-results">
          {items.length === 0 && <div className="px-3 py-4 text-sm text-[#7D8C85]" data-testid="manual-verify-empty">No supplier account matches "{q}".</div>}
          {items.map((u) => (
            <div key={u.id} className="px-3 py-2.5" data-testid={`manual-verify-row-${u.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px]">
                  <div className="font-medium text-[#11211A]">{u.company || u.name || "—"}</div>
                  <div className="text-[11px] text-[#7D8C85]">{u.email} · {u.tier || "free"} · {u.listing_count} listing{u.listing_count === 1 ? "" : "s"}{u.is_unclaimed ? " · unclaimed" : ""}{u.business_registration_number ? ` · ${u.business_registration_number_type || "Reg."} ${u.business_registration_number}` : ""}</div>
                </div>
                <VerificationStatusPill status={u.verification_status} verified={u.is_verified} />
                {u.is_verified ? (
                  <button onClick={() => setOpen(open === u.id ? null : u.id)} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border border-[#B35C4A] text-[#B35C4A] hover:bg-[#B35C4A] hover:text-white px-2.5 py-1.5 transition" data-testid={`manual-verify-manage-${u.id}`}><ShieldOff className="w-3 h-3" /> Manage</button>
                ) : (
                  <button onClick={() => setOpen(open === u.id ? null : u.id)} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-emerald-600 text-white hover:bg-emerald-700 px-2.5 py-1.5 transition" data-testid={`manual-verify-btn-${u.id}`}><ShieldCheck className="w-3 h-3" /> Verify</button>
                )}
              </div>
              {open === u.id && <div className="mt-3"><VerificationEditor target={u} onDone={done} onCancel={() => setOpen(null)} /></div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const VerificationQueue = ({ onChanged }) => {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [closing, setClosing] = useState(null);

  const closeReview = async (u) => {
    const msg = u.is_verified
      ? `Close this review? ${u.company || u.email} is already verified — they'll be removed from the pending queue and keep their badge.`
      : `Close this review without verifying ${u.company || u.email}? No email is sent; the request is marked closed.`;
    if (!window.confirm(msg)) return;
    setClosing(u.id);
    try {
      await api.patch(`/admin/suppliers/${u.id}/verification`, { action: "close" });
      toast.success("Review closed");
      if (open === u.id) setOpen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not close review");
    } finally {
      setClosing(null);
    }
  };

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get(`/admin/verifications?status=${status}`);
      setItems(data.items || []);
      onChanged && onChanged();
    } catch (e) {
      setItems([]);
      setLoadError(describeLoadError(e, "verification requests"));
      toast.error("Failed to load verifications");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);

  return (
    <div data-testid="admin-verification-queue">
      <ManualVerifyCard onChanged={onChanged} />
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div>
          <label className="block text-xs text-[#7D8C85] mb-1">Show</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-[#C4CFC9] bg-white text-sm rounded-sm" data-testid="verification-queue-filter">
            <option value="pending">Pending review</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
            <option value="all">All with a request</option>
          </select>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest border border-[#264839] text-[#264839] px-3 py-2 hover:bg-[#264839] hover:text-white transition" data-testid="verification-queue-refresh"><RefreshCw className="w-3 h-3" /> Refresh</button>
        <div className="text-xs text-[#7D8C85] ml-auto" data-testid="verification-queue-count">{items.length} supplier{items.length === 1 ? "" : "s"}</div>
      </div>
      <LoadErrorBanner error={loadError} onRetry={load} testId="verification-queue-error" />
      <div className="bg-white border border-[#E8ECE9] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F0EFEA] text-[#4A5550]">
            <tr className="text-left">
              <th className="px-3 py-2">Supplier</th>
              <th className="px-3 py-2">Registration</th>
              <th className="px-3 py-2">Submitted</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Listings</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#7D8C85]">Loading…</td></tr>}
            {!loading && !loadError && items.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#7D8C85]" data-testid="verification-queue-empty">Nothing here — no {status === "all" ? "" : status} verification requests.</td></tr>}
            {items.map((u) => (
              <React.Fragment key={u.id}>
              <tr className="border-t border-[#F0EFEA] align-top" data-testid={`verification-row-${u.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-[#11211A]">{u.company || u.name || "—"}</div>
                  <div className="text-[11px] text-[#7D8C85]">{u.email} · {u.tier || "free"}</div>
                </td>
                <td className="px-3 py-2 text-[#11211A]" data-testid={`verification-reg-${u.id}`}>
                  {u.business_registration_number ? <><span className="text-[10px] uppercase tracking-widest text-[#7D8C85] mr-1">{u.business_registration_number_type}</span>{u.business_registration_number}</> : <span className="text-[#7D8C85]">—</span>}
                </td>
                <td className="px-3 py-2 text-[#4A5550] text-xs">{(u.verification_requested_at || "").slice(0, 10) || "—"}</td>
                <td className="px-3 py-2">
                  <VerificationStatusPill status={u.verification_status} verified={u.is_verified} />
                  {u.verification_note && <div className="text-[11px] text-[#7D8C85] mt-1 max-w-[220px]">{u.verification_note}</div>}
                </td>
                <td className="px-3 py-2 text-xs">
                  {(u.listings || []).slice(0, 3).map((l) => (
                    <a key={l.id} href={`/listing/${l.slug || l.id}`} target="_blank" rel="noopener noreferrer" className="block text-[#264839] hover:underline truncate max-w-[200px]">{l.title}</a>
                  ))}
                  {(u.listings || []).length === 0 && <span className="text-[#7D8C85]">No listings</span>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setOpen(open === u.id ? null : u.id)} className="text-[10px] uppercase tracking-widest font-semibold border border-[#264839] text-[#264839] px-2 py-1 hover:bg-[#264839] hover:text-white transition" data-testid={`verification-review-${u.id}`}>{open === u.id ? "Hide" : "Review"}</button>
                    {u.verification_status === "pending" && (
                      <button onClick={() => closeReview(u)} disabled={closing === u.id} title={u.is_verified ? "Already verified — remove from the pending queue" : "Dismiss this request without verifying or emailing the supplier"} className="text-[10px] uppercase tracking-widest font-semibold border border-[#C4CFC9] text-[#7D8C85] px-2 py-1 hover:border-[#B35C4A] hover:text-[#B35C4A] transition disabled:opacity-50" data-testid={`verification-close-${u.id}`}>{closing === u.id ? "…" : "Close"}</button>
                    )}
                  </div>
                </td>
              </tr>
              {open === u.id && (
                <tr className="bg-[#FAFAF7]"><td colSpan={6} className="px-3 py-3">
                  <VerificationEditor target={{ ...u, user_id: u.id }} onDone={() => { setOpen(null); load(); }} onCancel={() => setOpen(null)} />
                </td></tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VerificationQueue;
